import { symRoundedList } from '@quasar/extras/material-symbols-rounded'
import { defineAsyncComponent } from 'vue'
import { useCrudManagerStore } from '../../stores/useCrudManagerStore'
import type { CrudInstance, CrudModule, Filters } from '../../types/AdvancedCrudManager'
import type { CrudListerInstanceData } from './types'
import { resolveForcedFilters } from './utils/forcedFilters'

export default {
  name: 'lister',
  slugs: ['list', 'liste'],
  icon: symRoundedList,
  titleIsPlural: true,
  mainComponent: defineAsyncComponent(() => import('./MainComponent.vue')),
  headerComponent: defineAsyncComponent(() => import('./components/ModuleHeaderComponent.vue')),

  /**
   * Boot function – creates initial instance data identical to legacy ListerOld so configs stay compatible.
   * Preserves pagination state if recent (within 30 minutes) to maintain user context during active use.
   */
  async boot(instance: CrudInstance) {
    const crudManagerStore = useCrudManagerStore()

    // Attempt to load default filters / columns from model config lazily
    let forcedFilters: Filters[] = []
    let visibleColumns: unknown[] = []

    try {
      const listerConfig = await crudManagerStore.getModelConfig(instance.modelName, 'lister')
      if (listerConfig) {
        const config = listerConfig as any
        visibleColumns = config.columns?.map((c: any) => ({ ...c, visible: c.visible ?? true })) || []
        forcedFilters = (config.filters as Filters[] | undefined)?.filter((f) => f.forced) ?? []
      }
    } catch (err) {
      console.warn(`Lister boot – failed to read config for ${instance.modelName}`, err)
    }

    // Check if we should preserve existing pagination state
    const existingData = instance.data as CrudListerInstanceData
    const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000 // 30 minutes in milliseconds
    const shouldPreservePagination = existingData?.pagination?.lastUsedAt && existingData.pagination.lastUsedAt > thirtyMinutesAgo

    // Default pagination for new instances or expired sessions
    const defaultPagination = {
      sortBy: 'created_at',
      descending: true,
      page: 1,
      rowsPerPage: 20,
      rowsNumber: 0,
      total: 0,
      requestTimeMS: 0,
      lastUsedAt: Date.now(), // Track when pagination was last used
    }

    instance.data = {
      ...(instance.data as CrudListerInstanceData),
      tableRef: null,
      listMode: existingData?.listMode || 'simpleCards', // Preserve list mode preference
      results: [],
      visibleColumns,
      // Filters start with model's forced filters; backend applies default_filters automatically
      filters: shouldPreservePagination
        ? resolveForcedFilters(forcedFilters, existingData.filters ?? [])
        : forcedFilters,
      response: {} as CrudListerInstanceData['response'],
      currentIndex: 0,
      pagination: shouldPreservePagination
        ? {
            ...existingData.pagination,
            lastUsedAt: Date.now(), // Update last used time
          }
        : defaultPagination,
      Query: shouldPreservePagination ? existingData.Query : '',
      sortBy: shouldPreservePagination ? existingData.sortBy : '',
    } as CrudListerInstanceData

    return instance
  },
} satisfies CrudModule
